import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useT, useLang } from '../i18n/LanguageContext';
import LanguageToggle from './LanguageToggle';

const PANELS = [
  {
    id: 'events',
    label: 'EVENTS',
    image: 'https://images.unsplash.com/photo-1763231575952-98244918f99b?w=1800&q=80',
    fallback: 'https://images.unsplash.com/photo-1712314947761-a8d718bd8c32?w=1800&q=80',
    line1: 'Where moments',
    line2: 'become legend.',
    sub: 'Gala evenings, brand activations, anniversaries, milestone celebrations.',
    href: '/events',
    testid: 'choice-panel-events',
  },
  {
    id: 'mice',
    label: 'MICE',
    image: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=1800&q=80',
    fallback: 'https://images.unsplash.com/photo-1505373877841-8d25f7d46678?w=1800&q=80',
    line1: 'Where decisions',
    line2: 'are made.',
    sub: 'Conferences, congresses, incentive groups, corporate retreats.',
    href: '/mice',
    testid: 'choice-panel-mice',
  },
  {
    id: 'weddings',
    label: 'WEDDINGS',
    image: 'https://images.unsplash.com/photo-1519741497674-611481863552?w=1800&q=80',
    fallback: 'https://images.unsplash.com/photo-1465495976277-4387d4b0e4a6?w=1800&q=80',
    line1: 'Where vows feel',
    line2: 'eternal.',
    sub: 'Destination weddings, intimate ceremonies, multi-day celebrations across Türkiye.',
    href: '/weddings',
    testid: 'choice-panel-weddings',
  },
];


const Panel = ({ panel, hovered, index, setHovered }) => {
  const isHovered = hovered === index;
  const isOther = hovered !== null && hovered !== index;
  const [imgSrc, setImgSrc] = useState(panel.image);

  // flex-grow: 3 (60%) when hovered, 1 (20%) when other panel hovered, 1 (33%) default
  const flexBasis = isHovered ? 3 : 1;
  const dim = isOther ? 0.55 : 1;

  const content = (
    <>
      {/* Photo */}
      <img
        src={imgSrc}
        alt={panel.label}
        onError={() => {
          if (panel.fallback && imgSrc !== panel.fallback) {
            setImgSrc(panel.fallback);
          }
        }}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
        style={{
          transform: isHovered ? 'scale(1.04)' : 'scale(1)',
        }}
      />

      {/* Navy gradient overlay, bottom 40% */}
      <div
        className="absolute inset-0 transition-opacity duration-700"
        style={{
          background:
            'linear-gradient(to bottom, rgba(10,19,47,0.35) 0%, rgba(10,19,47,0.15) 35%, rgba(10,19,47,0.55) 70%, rgba(10,19,47,0.92) 100%)',
        }}
      />

      {/* Vertical copper hairlines on sides (no gutters but a subtle separator) */}
      {index < PANELS.length - 1 && (
        <div className="hidden md:block absolute right-0 top-0 bottom-0 w-px bg-[rgba(214,168,122,0.18)] z-[3]" />
      )}

      {/* Content */}
      <div className="relative z-[2] h-full w-full flex flex-col items-center justify-center text-center px-6 md:px-8">
        <div
          className={`transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${
            isHovered ? 'translate-y-0 opacity-100' : 'translate-y-0 opacity-95'
          }`}
        >
          {/* Eyebrow */}
          <div
            className="mb-6 md:mb-8"
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '9pt',
              fontWeight: 600,
              letterSpacing: '0.32em',
              textTransform: 'uppercase',
              color: '#D6A87A',
            }}
          >
            {panel.eyebrow}
          </div>

          {/* Headline */}
          <h2
            className="text-white"
            style={{
              fontFamily: '"Cormorant Garamond", serif',
              fontSize: 'clamp(34px, 4.2vw, 56pt)',
              lineHeight: 1.04,
              fontWeight: 500,
              letterSpacing: '-0.005em',
            }}
          >
            <span className="block">{panel.line1}</span>
            <span
              className="block italic"
              style={{ color: '#D6A87A', fontWeight: 400 }}
            >
              {panel.line2}
            </span>
          </h2>

          {/* Sub */}
          <p
            className="mt-6 md:mt-8 mx-auto max-w-[36ch]"
            style={{
              fontFamily: '"Cormorant Garamond", serif',
              fontStyle: 'italic',
              fontWeight: 400,
              fontSize: 'clamp(14px, 1.05vw, 18px)',
              color: 'rgba(255,255,255,0.82)',
              lineHeight: 1.55,
            }}
          >
            “{panel.sub}”
          </p>

          {/* Subtle copper underline that draws on hover */}
          <div className="mt-8 md:mt-10 flex justify-center">
            <span
              className="block h-px bg-[#D6A87A] transition-all duration-700 ease-out"
              style={{ width: isHovered ? '88px' : '40px' }}
            />
          </div>
        </div>
      </div>
    </>
  );

  const wrapperClass =
    'relative overflow-hidden cursor-pointer h-full transition-[flex-grow,filter,opacity] duration-[700ms] ease-[cubic-bezier(0.22,1,0.36,1)]';

  const wrapperStyle = {
    flexGrow: flexBasis,
    flexBasis: 0,
    filter: `brightness(${dim})`,
  };

  const onEnter = () => setHovered(index);
  const onLeave = () => setHovered(null);

  // Use Link for internal routes, anchor for hash links
  if (panel.href.startsWith('#')) {
    return (
      <a
        href={panel.href}
        data-testid={panel.testid}
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
        onFocus={onEnter}
        onBlur={onLeave}
        className={wrapperClass}
        style={wrapperStyle}
      >
        {content}
      </a>
    );
  }
  return (
    <Link
      to={panel.href}
      data-testid={panel.testid}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onFocus={onEnter}
      onBlur={onLeave}
      className={wrapperClass}
      style={wrapperStyle}
    >
      {content}
    </Link>
  );
};

const ChoiceHero = () => {
  const [hovered, setHovered] = useState(null);
  const t = useT();
  const { dir } = useLang();

  // Build live, translated panel content from the i18n dictionary
  const panels = [
    {
      ...PANELS[0],
      eyebrow: t('choiceHero.events.eyebrow'),
      line1: t('choiceHero.events.line1'),
      line2: t('choiceHero.events.line2'),
      sub: t('choiceHero.events.sub'),
    },
    {
      ...PANELS[1],
      eyebrow: t('choiceHero.mice.eyebrow'),
      line1: t('choiceHero.mice.line1'),
      line2: t('choiceHero.mice.line2'),
      sub: t('choiceHero.mice.sub'),
    },
    {
      ...PANELS[2],
      eyebrow: t('choiceHero.weddings.eyebrow'),
      line1: t('choiceHero.weddings.line1'),
      line2: t('choiceHero.weddings.line2'),
      sub: t('choiceHero.weddings.sub'),
    },
  ];

  const stripLinks = [
    { labelKey: 'mice', href: '/mice' },
    { labelKey: 'events', href: '/events' },
    { labelKey: 'weddings', href: '/weddings' },
    { labelKey: 'portfolio', href: '/portfolio' },
    { labelKey: 'studio', href: '/studio' },
    { labelKey: 'contact', href: '/contact' },
  ];

  return (
    <section className="relative w-full" data-testid="choice-hero" dir={dir}>
      {/* Three panels, full viewport height */}
      <div className="flex flex-col md:flex-row w-full h-screen">
        {panels.map((p, i) => (
          <Panel
            key={p.id}
            panel={p}
            index={i}
            hovered={hovered}
            setHovered={setHovered}
          />
        ))}
      </div>

      {/* 60px navy strip, sticky after hero */}
      <div
        className="sticky top-0 z-40 w-full"
        style={{ height: '60px', backgroundColor: '#0A132F' }}
        data-testid="choice-hero-strip"
      >
        <div className="h-full max-w-[1600px] mx-auto px-5 sm:px-8 flex items-center justify-between gap-4">
          {/* Left: brand mark */}
          <Link to="/" className="flex items-center" aria-label="GlowMeets home" data-testid="strip-logo">
            <img
              src="/glowmeets-mark.png"
              alt="GlowMeets"
              style={{ height: '34px', width: 'auto', display: 'block' }}
            />
          </Link>

          {/* Center: minimalist nav */}
          <nav className="hidden lg:flex items-center" data-testid="strip-nav">
            {stripLinks.map((link, idx) => (
              <React.Fragment key={link.labelKey}>
                <Link
                  to={link.href}
                  className="hover:text-bone transition-colors"
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '11pt',
                    letterSpacing: '0.22em',
                    textTransform: 'uppercase',
                    color: '#D6A87A',
                    fontWeight: 500,
                  }}
                >
                  {t(`nav.${link.labelKey}`)}
                </Link>
                {idx < stripLinks.length - 1 && (
                  <span
                    className="mx-3 select-none"
                    style={{ color: 'rgba(214,168,122,0.55)', fontSize: '11pt' }}
                  >
                    ·
                  </span>
                )}
              </React.Fragment>
            ))}
          </nav>

          {/* Right: language toggle + CTA */}
          <div className="flex items-center gap-5">
            <div className="hidden md:flex" data-testid="strip-lang">
              <LanguageToggle variant="strip" />
            </div>

            <Link
              to="/contact"
              data-testid="strip-cta"
              className="inline-flex items-center justify-center transition-colors"
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '10pt',
                fontWeight: 600,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: '#D6A87A',
                border: '1px solid #D6A87A',
                padding: '8px 18px',
                borderRadius: '999px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#D6A87A';
                e.currentTarget.style.color = '#0A132F';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#D6A87A';
              }}
            >
              {t('cta.bookConsultation')}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ChoiceHero;
